import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, map, combineLatest, distinctUntilChanged } from 'rxjs';
import { GridSettingsFacade } from 'src/app/features/grid/classes/grid-settings.facade';
import { PapaParseExportService, ExportOptions } from 'src/app/features/grid/services/papa-parse-export-service.service';
import { GridColumn } from './column.interface';
import { GridAggregateService } from '../services/grid-aggregate-service.service';

export interface ExportStats {
  totalRecords: number;
  filteredRecords: number;
  selectedRecords: number;
  visibleColumns: number;
  hasActiveFilters: boolean;
  hasAggregates: boolean;

  canExportAll: boolean;
  canExportFiltered: boolean;
  canExportSelected: boolean;
}

export interface ExportContext {
  stats: ExportStats;
  visibleColumns: GridColumn[];
  fileName: string;
}

export interface ExportResult {
  success: boolean;
  format: string;
  recordCount: number;
  fileName: string;
  exportType: 'all' | 'filtered' | 'selected';
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExportManagerFacade {

  //#region Injected Services
  private readonly exportService = inject(PapaParseExportService);
  private readonly aggregateService = inject(GridAggregateService);
  //#endregion

  // //#region State Signals
  // private readonly isExportingSignal = signal<boolean>(false);
  // private readonly lastExportResultSignal = signal<ExportResult | null>(null);
  // private readonly gridFacadeSignal = signal<GridSettingsFacade | null>(null);
  
  // // Public readonly accessors
  // readonly isExporting = this.isExportingSignal.asReadonly();
  // readonly lastExportResult = this.lastExportResultSignal.asReadonly();
  // readonly gridFacade = this.gridFacadeSignal.asReadonly();
  // //#endregion
  
  // State signals
  private readonly isExporting = signal<boolean>(false);
  private readonly lastExportResult = signal<ExportResult | null>(null);
  
  // Public observables
  readonly isExporting$ = computed(() => this.isExporting());
  readonly lastExportResult$ = computed(() => this.lastExportResult());
  
  constructor( private gridFacade: GridSettingsFacade ) {}

  /**
   * Initialize the facade with a grid facade instance
   */
  initialize(gridFacade: GridSettingsFacade): void {
    this.gridFacade = gridFacade;
    //this.gridFacadeSignal.set(gridFacade);
  }

  /**
   * Check if facade is initialized
   */
  private ensureInitialized(): GridSettingsFacade {
    //const facade = this.gridFacade();
    const facade = this.gridFacade;
    if (!facade) {
      throw new Error('ExportManagerFacade not initialized. Call initialize() first.');
    }
    return facade;
  }

  //private gridFacade?: GridSettingsFacade;

  /**
   * Get comprehensive export statistics
   */
  getExportStats(): Observable<ExportStats> {
    if (!this.gridFacade) {
      throw new Error('ExportManagerFacade not initialized. Call initialize() first.');
    }

    return combineLatest([
      this.gridFacade.state$,
      this.gridFacade.filteredData$,
      this.gridFacade.hasActiveFilters$,
    ]).pipe(
      map(([state, filteredData, hasActiveFilters]) => ({
        totalRecords: state.settings.data.length,
        filteredRecords: filteredData.length,
        selectedRecords: state.selectedItems.length,
        visibleColumns: state.settings.getVisibleColumns().length,
        hasActiveFilters,
        hasAggregates: this.gridFacade.hasColumnAggregates(),
        canExportAll: state.settings.data.length > 0,
        canExportFiltered: hasActiveFilters && filteredData.length > 0,
        canExportSelected: state.selectedItems.length > 0,
      })),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    );
  }

  /**
   * Get complete export context for the export manager
   */
  getExportContext(): Observable<ExportContext> {
    if (!this.gridFacade) {
      throw new Error('ExportManagerFacade not initialized. Call initialize() first.');
    }

    return combineLatest([
      this.getExportStats(),
      this.gridFacade.visibleColumns$,
    ]).pipe(
      map(([stats, visibleColumns]) => ({
        stats,
        visibleColumns,
        fileName: this.getRecommendedFileName('all'),
      })),
    );
  }

  /**
   * Export all data
   */
  async exportAllPromise(options: ExportOptions, fileName?: string): Promise<ExportResult> {
    if (!this.gridFacade) {
      return this.createErrorResult('Facade not initialized', 'all');
    }

    this.isExporting.set(true);

    try {
      const settings = this.gridFacade.getCurrentSettings();
      const finalFileName = fileName || this.getRecommendedFileName('all');
      
      await this.exportService.exportToCSVUpdated(
        settings.data,
        settings.getVisibleColumns(),
        finalFileName,
        settings.export.header || 'Export',
        settings.export.renderTemplates || true,
        true,
        options,
      );

      const result: ExportResult = {
        success: true,
        format: 'csv',
        recordCount: settings.data.length,
        fileName: finalFileName,
        exportType: 'all',
      };

      this.lastExportResult.set(result);
      return result;
    } catch (error) {
      return this.createErrorResult(error.message, 'all');
    } finally {
      this.isExporting.set(false);
    }
  }

  /**
   * Export filtered data
   */
  async exportFilteredPromise(options: ExportOptions, fileName?: string): Promise<ExportResult> {
    if (!this.gridFacade) {
      return this.createErrorResult('Facade not initialized', 'filtered');
    }

    this.isExporting.set(true);

    try {
      const settings = this.gridFacade.getCurrentSettings();
      //const filteredData = await this.gridFacade.getFilteredData().toPromise();
      const filteredData = await this.gridFacade.getFilteredData();
      const finalFileName = fileName || this.getRecommendedFileName('filtered');
      
      if (!filteredData || filteredData.length === 0) {
        throw new Error('No filtered data available to export');
      }

      await this.exportService.exportToCSVUpdated(
        filteredData,
        settings.getVisibleColumns(),
        finalFileName,
        settings.export.header || 'Export',
        settings.export.renderTemplates || true,
        true,
        options,
      );

      const result: ExportResult = {
        success: true,
        format: 'csv',
        recordCount: filteredData.length,
        fileName: finalFileName,
        exportType: 'filtered',
      };

      this.lastExportResult.set(result);
      return result;
    } catch (error) {
      return this.createErrorResult(error.message, 'filtered');
    } finally {
      this.isExporting.set(false);
    }
  }

  /**
   * Export selected data
   */
  async exportSelectedPromise(options: ExportOptions, fileName?: string): Promise<ExportResult> {
    if (!this.gridFacade) {
      return this.createErrorResult('Facade not initialized', 'selected');
    }

    this.isExporting.set(true);

    try {
      const settings = this.gridFacade.getCurrentSettings();
      const selectedData = this.gridFacade.getSelectedItems();
      const finalFileName = fileName || this.getRecommendedFileName('selected');
      
      if (selectedData.length === 0) {
        throw new Error('No selected data available to export');
      }

      // Update custom labels for selected data
      const selectedOptions: ExportOptions = {
        ...options,
        customAggregateLabels: {
          ...options.customAggregateLabels,
          sum: 'Selected Total',
          avg: 'Selected Average',
          count: 'Selected Count',
        },
      };

      await this.exportService.exportToCSVUpdated(
        selectedData,
        settings.getVisibleColumns(),
        finalFileName,
        settings.export.header || 'Export',
        settings.export.renderTemplates || true,
        true,
        selectedOptions,
      );

      const result: ExportResult = {
        success: true,
        format: 'csv',
        recordCount: selectedData.length,
        fileName: finalFileName,
        exportType: 'selected',
      };

      this.lastExportResult.set(result);
      return result;
    } catch (error) {
      return this.createErrorResult(error.message, 'selected');
    } finally {
      this.isExporting.set(false);
    }
  }

  // Export methods with enhanced options
  exportAll(options: ExportOptions, fileName?: string): void {
    this.gridFacade.exportToCSVUpdated(fileName, false, options.includeAggregates);
  }

  exportFiltered(options: ExportOptions, fileName?: string): void {
    this.gridFacade.exportToCSVUpdated(fileName, false, options.includeAggregates);
  }

  exportSelected(options: ExportOptions, fileName?: string): void {
    this.gridFacade.exportSelectedToCSVUpdated(fileName, options.includeAggregates);
  }

  //#region Preview
  /**
   * Generate preview data
   */
  generatePreviewObservable(maxRows: number, options: ExportOptions): Observable<any[]> {
    // if (!this.gridFacade) {
    //   throw new Error('ExportManagerFacade not initialized. Call initialize() first.');
    // }

    // return this.gridFacade.generateExportPreview(maxRows, options.includeAggregates);
    const facade = this.ensureInitialized();
    return facade.filteredData$.pipe(
      map(filteredData => {
        const settings = facade.getCurrentSettings();
        const dataToPreview = filteredData.length > 0 ? filteredData : settings.data;

        const exportOptions: ExportOptions = {
          includeAggregates: options.includeAggregates,
          aggregatePosition: 'bottom',
          aggregateSeparatorRow: true,
          flattenNestedHeaders: true,
        };

        return this.exportService.generateExportPreview(
          dataToPreview,
          settings.getVisibleColumns(),
          maxRows,
          exportOptions,
        );
      }),
    );
  }

  generatePreview(maxRows: number, options: ExportOptions): any[] {
    const facade = this.ensureInitialized();
    return facade.generateExportPreview(maxRows, options.includeAggregates);
  }
  //#endregion Preview

  //#region Util Functions
  /**
   * Check if can export selected data
   */
  canExportSelected(): Observable<boolean> {
    if (!this.gridFacade) {
      throw new Error('ExportManagerFacade not initialized. Call initialize() first.');
    }

    return this.gridFacade.selectedItems$.pipe(
      map(items => items.length > 0),
    );
  }

  /**
   * Check if can export filtered data
   */
  canExportFiltered(): Observable<boolean> {
    if (!this.gridFacade) {
      throw new Error('ExportManagerFacade not initialized. Call initialize() first.');
    }

    return this.gridFacade.hasActiveFilters$;
  }

  /**
   * Get recommended file name based on export type
   */
  getRecommendedFileName(exportType: 'all' | 'filtered' | 'selected'): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const settings = this.gridFacade.getCurrentSettings();
    const baseName = settings.export.fileName || 'export';
    
    switch (exportType) {
      case 'selected':
        return `${baseName}_selected_${timestamp}.csv`;
      case 'filtered':
        return `${baseName}_filtered_${timestamp}.csv`;
      default:
        return `${baseName}_${timestamp}.csv`;
    }
  }

  /**
   * Get export format options based on available data
   */
  getAvailableExportFormats(): Observable<Array<{ label: string; value: string; disabled: boolean }>> {
    return this.getExportStats().pipe(
      map(stats => [
        {
          label: `Export All (${stats.totalRecords} records)`,
          value: 'all',
          disabled: !stats.canExportAll,
        },
        {
          label: `Export Filtered (${stats.filteredRecords} records)`,
          value: 'filtered',
          disabled: !stats.canExportFiltered,
        },
        {
          label: `Export Selected (${stats.selectedRecords} records)`,
          value: 'selected',
          disabled: !stats.canExportSelected,
        },
      ]),
    );
  }

  /**
   * Create default export options based on context
   */
  createDefaultOptions(exportType: 'all' | 'filtered' | 'selected'): ExportOptions {
    const hasAggregates = this.gridFacade ? 
      this.aggregateService.hasColumnAggregates(
        this.gridFacade.getCurrentSettings().getVisibleColumns(),
      ) : false;

    return {
      includeAggregates: hasAggregates,
      aggregatePosition: 'bottom',
      aggregateSeparatorRow: true,
      nestedColumnSeparator: ' - ',
      flattenNestedHeaders: true,
      customAggregateLabels: this.getDefaultLabels(exportType),
    };
  }

  /**
   * Get default aggregate labels based on export type
   */
  private getDefaultLabels(exportType: 'all' | 'filtered' | 'selected'): Record<string, string> {
    const prefix = exportType === 'selected' ? 'Selected ' : exportType === 'filtered' ? 'Filtered ' : '';
    
    return {
      sum: `${prefix}Total`,
      avg: `${prefix}Average`,
      count: `${prefix}Count`,
      min: `${prefix}Minimum`,
      max: `${prefix}Maximum`,
    };
  }
  //#endregion Util Functions

  /**
   * Create error result
   */
  private createErrorResult(message: string, exportType: 'all' | 'filtered' | 'selected'): ExportResult {
    const result: ExportResult = {
      success: false,
      format: 'csv',
      recordCount: 0,
      fileName: '',
      exportType,
      error: message,
    };
    
    this.lastExportResult.set(result);
    return result;
  }

  /**
   * Reset state
   */
  reset(): void {
    this.isExporting.set(false);
    this.lastExportResult.set(null);
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    this.reset();
    this.gridFacade = undefined;
  }
}